import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Github, Layers, Wrench, ListChecks, GraduationCap, ShieldAlert } from "lucide-react";

const Block = ({ icon: Icon, title, children, testId }) => (
    <div data-testid={testId} className="border border-white/10 bg-black/30 p-5">
        <h4 className="mb-3 flex items-center gap-2 font-mono2 text-xs uppercase tracking-[0.2em] text-neon">
            <Icon size={14} strokeWidth={1.5} />
            {title}
        </h4>
        {children}
    </div>
);

export const ProjectModal = ({ project, onClose }) => {
    useEffect(() => {
        if (!project) return undefined;
        document.body.style.overflow = "hidden";
        window.__lenis?.stop();
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.__lenis?.start();
            window.removeEventListener("keydown", onKey);
        };
    }, [project, onClose]);

    return (
        <AnimatePresence>
            {project && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-0 backdrop-blur-sm sm:items-center sm:p-6"
                    onClick={onClose}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${project.title} details`}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 48 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 48 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="project-modal"
                        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto border border-white/10 bg-surface shadow-[0_0_80px_rgba(0,255,102,0.08)]"
                    >
                        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-black/70 px-6 py-4 backdrop-blur-xl">
                            <p className="font-mono2 text-xs text-zinc-500">
                                <span className="text-neon">~/projects/</span>
                                {project.slug}
                            </p>
                            <button
                                type="button"
                                data-testid="project-modal-close-btn"
                                onClick={onClose}
                                aria-label="Close project details"
                                className="flex h-9 w-9 items-center justify-center border border-white/10 text-zinc-400 transition-colors duration-300 hover:border-neon/50 hover:text-neon"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div className="space-y-5 px-6 py-7 sm:px-8">
                            <div>
                                <div className="mb-3 flex flex-wrap items-center gap-3">
                                    <span className="font-mono2 text-sm text-neon">{project.id}</span>
                                    <span className="border border-cyber/40 bg-cyber/5 px-2.5 py-1 font-mono2 text-[11px] text-cyber">
                                        {project.type}
                                    </span>
                                </div>
                                <h3 className="font-display text-2xl font-extrabold tracking-tighter text-zinc-100 sm:text-3xl">
                                    {project.title}
                                </h3>
                            </div>

                            <Block icon={Layers} title="Overview" testId="project-modal-overview">
                                <p className="text-sm leading-relaxed text-zinc-400">{project.overview}</p>
                            </Block>

                            <Block icon={Wrench} title="Technologies" testId="project-modal-technologies">
                                <ul className="flex flex-wrap gap-2">
                                    {project.tech.map((t) => (
                                        <li key={t} className="border border-white/10 bg-black/40 px-3 py-1.5 font-mono2 text-xs text-zinc-300">
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                            </Block>

                            <Block icon={ListChecks} title="Key Features" testId="project-modal-features">
                                <ul className="space-y-2">
                                    {project.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-400">
                                            <span className="mt-1.5 h-1 w-1 shrink-0 bg-neon/70" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            </Block>

                            <Block icon={GraduationCap} title="What I Learned" testId="project-modal-learned">
                                <p className="text-sm leading-relaxed text-zinc-400">{project.learned}</p>
                            </Block>

                            <Block icon={ShieldAlert} title="Security Considerations" testId="project-modal-security">
                                <p className="text-sm leading-relaxed text-zinc-400">{project.security}</p>
                            </Block>

                            <div className="pt-2">
                                {project.github ? (
                                    <a
                                        href={project.github}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="project-modal-github-link"
                                        className="inline-flex items-center gap-2 border border-neon px-5 py-3 font-mono2 text-sm text-neon transition-colors duration-300 hover:bg-neon hover:text-black"
                                    >
                                        <Github size={15} strokeWidth={1.5} />
                                        {project.github.endsWith("itsharshgill") ? "GitHub Profile" : "GitHub Repository"}
                                    </a>
                                ) : (
                                    <span
                                        data-testid="project-modal-github-placeholder"
                                        className="inline-flex cursor-not-allowed items-center gap-2 border border-white/10 px-5 py-3 font-mono2 text-sm text-zinc-600"
                                        aria-disabled="true"
                                    >
                                        <Github size={15} strokeWidth={1.5} />
                                        GitHub Repository — To Be Added
                                    </span>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
